import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, User, Video, Package, Clock, Sparkles, Hash, AtSign, TrendingUp, FileText } from 'lucide-react';
import api from '../../services/api';
import { buildAvatarUrl } from '../../utils/imageUrl';
import { getImageUrl } from '../../utils/mediaUrl';
import { auctionPath, profilePath, postPath, streamPath } from '../../utils/publicIds';

interface ModernSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SearchResult {
  users: any[];
  auctions: any[];
  streams: any[];
  posts?: any[];
  total_results: number;
}

type ResultThumbType = 'user' | 'auction' | 'post' | 'stream';

const getFallbackLabel = (label?: string) => {
  const trimmed = (label || '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const cleanPreviewText = (value?: string) => (value || '').replace(/\s+/g, ' ').trim();

const getPostPreview = (post: any) => {
  return cleanPreviewText(post?.content || post?.caption || post?.text || post?.body || post?.title) || 'Text post';
};

const getPostAuthor = (post: any) => cleanPreviewText(post?.user?.username || post?.username || post?.author?.username);

const getPostThumb = (post: any) => {
  const media = Array.isArray(post?.media) ? post.media[0] : null;
  return getImageUrl(
    media?.thumbnail_url ||
    media?.media_url ||
    post?.thumbnail_url ||
    post?.image_url ||
    post?.media_url
  ) || undefined;
};

const ResultThumb: React.FC<{
  src?: string;
  label?: string;
  type: ResultThumbType;
  children?: React.ReactNode;
}> = ({ src, label, type, children }) => {
  const [failed, setFailed] = useState(false);
  const fallbackClass: Record<ResultThumbType, string> = {
    user: 'rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 border-2 border-emerald-500/30',
    auction: 'rounded-xl bg-gradient-to-br from-pink-500 to-rose-600',
    post: 'rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600',
    stream: 'rounded-xl bg-gradient-to-br from-red-500 to-pink-600',
  };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={label || ''}
        onError={() => setFailed(true)}
        className={`w-9 h-9 object-cover flex-shrink-0 ${type === 'user' ? 'rounded-full border-2 border-emerald-500/30' : 'rounded-xl'}`}
      />
    );
  }

  return (
    <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center text-white font-black shadow-sm ${fallbackClass[type]}`}>
      {children || <span className="text-sm">{getFallbackLabel(label)}</span>}
    </div>
  );
};

const ModernSearchModal: React.FC<ModernSearchModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'users' | 'social' | 'auctions' | 'streams'>('all');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) setRecentSearches(JSON.parse(saved));
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    if (!isOpen) setQuery('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const performSearch = async () => {
      if (query.length < 2) { setResults(null); return; }
      setLoading(true);
      try {
        // Always search both social posts AND global (users/auctions/streams)
        const [socialRes, globalRes] = await Promise.allSettled([
          api.get('/news-feed/search', { params: { q: query, limit: 10 } }),
          api.get('/search/', { params: { q: query, search_type: activeTab === 'all' || activeTab === 'social' ? undefined : activeTab, limit: 20 } }),
        ]);
        const global = globalRes.status === 'fulfilled' ? globalRes.value.data : { users: [], auctions: [], streams: [], total_results: 0 };
        const posts = socialRes.status === 'fulfilled' ? socialRes.value.data : [];
        setResults({ ...global, posts, total_results: (global.total_results || 0) + (Array.isArray(posts) ? posts.length : 0) });

      } catch { setResults(null); }
      finally { setLoading(false); }
    };
    const t = setTimeout(performSearch, 300);
    return () => clearTimeout(t);
  }, [query, activeTab]);

  const saveRecentSearch = (q: string) => {
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 6);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  const handleNavigate = (type: string, id: number | string) => {
    saveRecentSearch(query);
    onClose();
    if (type === 'user') navigate(profilePath(id));
    else if (type === 'auction') navigate(auctionPath(id));
    else if (type === 'stream') navigate(streamPath(id));
    else if (type === 'post') navigate(postPath(id));
    else if (type === 'social') navigate(`/feed?q=${encodeURIComponent(String(id))}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    saveRecentSearch(query);
    onClose();
    if (query.startsWith('@') || query.startsWith('#') || activeTab === 'social') {
      navigate(`/feed?q=${encodeURIComponent(query)}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'all', label: 'All', icon: Sparkles },
    { id: 'social', label: 'Social', icon: TrendingUp },
    { id: 'users', label: 'Creators', icon: User },
    { id: 'auctions', label: 'Game-a-Rama', icon: Package },
    { id: 'streams', label: 'Live', icon: Video },
  ];

  const hasResults = results && (results.total_results > 0 || (results.posts?.length || 0) > 0);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-16 sm:pt-20 mobile-modal-viewport">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        {/* Modal box */}
        <motion.div
          initial={{ opacity: 0, y: -16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.97 }}
          transition={{ type: 'spring', damping: 30, stiffness: 350 }}
          className="relative w-full max-w-2xl bg-white dark:bg-[#0d1333] rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-[#1e2a5e] mobile-modal-sheet"
        >
          {/* ── Search input row ── */}
          <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-[#1e2a5e]">
            <Search className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search creators, @mentions, #tags, Game-a-Rama..."
              className="flex-1 text-base font-medium bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 min-w-0"
              autoComplete="off"
              spellCheck="false"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1e2a5e] transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1e2a5e] transition-colors ml-1">
              <X className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-white" />
            </button>
          </form>

          {/* ── Tabs ── */}
          <div className="flex gap-1 px-3 pt-2 pb-1 border-b border-gray-100 dark:border-[#1e2a5e] overflow-x-auto scrollbar-none">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${activeTab === tab.id
                  ? 'bg-emerald-500 text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1e2a5e] hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Results / Empty state ── */}
          <div className="max-h-[60vh] overflow-y-auto mobile-modal-scroll mobile-modal-scroll-pad">
            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center gap-3 py-12">
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-400 font-medium">Searching...</span>
              </div>
            )}

            {/* Short query — recent searches */}
            {!loading && query.length < 2 && (
              <div className="p-4">
                {recentSearches.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2 px-1">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> Recent
                      </span>
                      <button
                        onClick={() => { setRecentSearches([]); localStorage.removeItem('recentSearches'); }}
                        className="text-xs text-gray-400 hover:text-red-400 transition-colors font-medium"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-0.5">
                      {recentSearches.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => setQuery(s)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all text-left group"
                        >
                          <Clock className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{s}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Hints */}
                <div className="px-1 py-6 text-center">
                  <p className="text-sm text-gray-400 font-medium">
                    Try <span className="text-emerald-500 font-bold">@username</span> or <span className="text-teal-500 font-bold">#hashtag</span> for social search
                  </p>
                </div>
              </div>
            )}

            {/* No results */}
            {!loading && query.length >= 2 && !hasResults && (
              <div className="py-14 text-center">
                <div className="w-12 h-12 bg-gray-100 dark:bg-[#131a45] rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Search className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                </div>
                <p className="text-base font-semibold text-gray-600 dark:text-gray-400">No results for "<span className="text-gray-900 dark:text-white">{query}</span>"</p>
                <p className="text-sm text-gray-400 mt-1">Try different keywords</p>
              </div>
            )}

            {/* Results */}
            {!loading && hasResults && (
              <div className="p-3 space-y-4">

                {/* Social / @ / # quick actions */}
                {(activeTab === 'all' || activeTab === 'social') && (query.startsWith('@') || query.startsWith('#')) && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5">Social Feed</p>
                    <button
                      onClick={() => handleNavigate('social', query)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${query.startsWith('@')
                        ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                        }`}
                    >
                      {query.startsWith('@') ? <AtSign className="w-5 h-5" /> : <Hash className="w-5 h-5" />}
                      <div>
                        <p className="font-semibold text-sm">Search feed for {query}</p>
                        <p className="text-xs opacity-80">
                          {query.startsWith('@') ? 'Posts by or mentioning this user' : 'Posts with this hashtag'}
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Users */}
                {(activeTab === 'all' || activeTab === 'users') && (results?.users?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5 flex items-center gap-1.5">
                      <User className="w-3 h-3" /> Creators
                    </p>
                    <div className="space-y-0.5">
                      {results!.users.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleNavigate('user', user.username)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all text-left group"
                        >
                          <ResultThumb
                            src={buildAvatarUrl(user.avatar_url, false) || undefined}
                            label={user.username}
                            type="user"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-emerald-500 transition-colors truncate">@{user.username}</p>
                            <p className="text-xs text-gray-400">{user.role === 'model' ? 'Creator' : 'Member'}</p>
                          </div>
                          {user.is_verified && <Sparkles className="w-4 h-4 text-blue-400 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auctions */}
                {(activeTab === 'all' || activeTab === 'auctions') && (results?.auctions?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5 flex items-center gap-1.5">
                      <Package className="w-3 h-3" /> Game-a-Rama
                    </p>
                    <div className="space-y-0.5">
                      {results!.auctions.map((auction) => (
                        <button
                          key={auction.id}
                          onClick={() => handleNavigate('auction', auction.id)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all text-left group"
                        >
                          <ResultThumb label={auction.title} type="auction">
                            <Package className="w-4 h-4 text-white" />
                          </ResultThumb>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-pink-500 transition-colors truncate">{auction.title}</p>
                            <p className="text-xs text-gray-400 truncate">{auction.description}</p>
                          </div>
                          <p className="text-sm font-bold text-emerald-500 flex-shrink-0">{auction.current_bid} 🪙</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Posts */}
                {(activeTab === 'all' || activeTab === 'social') && (results?.posts?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5 flex items-center gap-1.5">
                      <Hash className="w-3 h-3" /> Posts
                    </p>
                    <div className="space-y-0.5">
                      {results!.posts!.slice(0, 6).map((post: any) => {
                        const author = getPostAuthor(post);
                        const preview = getPostPreview(post);
                        const thumb = getPostThumb(post);

                        return (
                          <button
                            key={post.id}
                            onClick={() => post.id ? handleNavigate('post', post.id) : handleNavigate('social', author ? `@${author}` : query)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all text-left group"
                          >
                            <ResultThumb
                              src={thumb}
                              label={preview}
                              type="post"
                            >
                              <FileText className="w-4 h-4 text-white" />
                            </ResultThumb>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-teal-500 transition-colors truncate">
                                {author ? `@${author}` : 'Post'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{preview}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-[10px] text-gray-400">{post.likes_count || 0} ❤️</p>
                              <p className="text-[10px] text-gray-400">{post.comments_count || 0} 💬</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Streams */}
                {(activeTab === 'all' || activeTab === 'streams') && (results?.streams?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-2 mb-1.5 flex items-center gap-1.5">
                      <Video className="w-3 h-3" /> Live Now
                    </p>
                    <div className="space-y-0.5">
                      {results!.streams.map((stream) => (
                        <button
                          key={stream.id}
                          onClick={() => handleNavigate('stream', stream.id)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#131a45] transition-all text-left group"
                        >
                          <div className="relative flex-shrink-0">
                            <ResultThumb label={stream.title || 'Live'} type="stream">
                              <Video className="w-4 h-4 text-white" />
                            </ResultThumb>
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-[#0d1333] animate-pulse" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-red-500 transition-colors truncate">{stream.title || 'Live Stream'}</p>
                            <p className="text-xs text-gray-400">{stream.viewer_count || 0} watching</p>
                          </div>
                          <span className="text-[10px] font-black px-1.5 py-0.5 bg-red-500 text-white rounded-full flex-shrink-0">LIVE</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-[#1e2a5e] bg-gray-50/50 dark:bg-[#0a0e27]/40">
            <div className="flex gap-3 text-[11px] text-gray-400 font-medium">
              <span><kbd className="bg-gray-200 dark:bg-[#1e2a5e] text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded text-[10px] font-mono mr-1">esc</kbd>Close</span>
              <span><kbd className="bg-gray-200 dark:bg-[#1e2a5e] text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded text-[10px] font-mono mr-1">↵</kbd>Search</span>
            </div>
            {results && (
              <span className="text-[11px] text-gray-400 font-medium">{results.total_results || 0} results</span>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ModernSearchModal;
